import { useState } from "react";
import { toast } from "sonner";
import ImageInsertModal from "./ImageInsertModal";

// Insert `insertText` into `textarea` at the current cursor position (replacing any selection),
// then call `setValue` with the new value and move the caret to just after the inserted text.
function insertAtCaret(textarea, value, setValue, insertText) {
  const start = textarea.selectionStart ?? value.length;
  const end = textarea.selectionEnd ?? value.length;
  const before = value.slice(0, start);
  const after = value.slice(end);
  const next = `${before}${insertText}${after}`;
  setValue(next);
  requestAnimationFrame(() => {
    textarea.focus();
    const caret = start + insertText.length;
    textarea.setSelectionRange(caret, caret);
  });
}

// Wrap the current selection with `*...*`. If nothing is selected, insert a
// `*italic text*` placeholder at the cursor and select the placeholder text so
// the writer can immediately overtype it.
function wrapSelectionItalic(textarea, value, setValue) {
  const start = textarea.selectionStart ?? value.length;
  const end = textarea.selectionEnd ?? value.length;
  const selected = value.slice(start, end);
  if (selected) {
    const before = value.slice(0, start);
    const after = value.slice(end);
    const next = `${before}*${selected}*${after}`;
    setValue(next);
    requestAnimationFrame(() => {
      textarea.focus();
      // Place caret just after the closing `*` so typing continues in normal weight.
      const caret = end + 2;
      textarea.setSelectionRange(caret, caret);
    });
  } else {
    const placeholder = "italic text";
    const insertText = `*${placeholder}*`;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const next = `${before}${insertText}${after}`;
    setValue(next);
    requestAnimationFrame(() => {
      textarea.focus();
      // Select the placeholder so the writer can overtype it immediately.
      textarea.setSelectionRange(start + 1, start + 1 + placeholder.length);
    });
  }
}

export default function MarkdownToolbar({ textareaRef, value, onChange, linkOnly = false, hideLink = false }) {
  const [imgOpen, setImgOpen] = useState(false);

  const insertLink = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const selected = value.slice(textarea.selectionStart, textarea.selectionEnd);
    const label = window.prompt("Link text (what readers will click):", selected || "");
    if (label === null) return;
    const clean = label.trim();
    if (!clean) {
      toast.error("Link needs some text.");
      return;
    }
    const url = window.prompt("Link URL (https://... or an internal path like /polls):", "https://");
    if (url === null) return;
    const cleanUrl = url.trim();
    if (!cleanUrl || cleanUrl === "https://") {
      toast.error("Link needs a URL.");
      return;
    }
    insertAtCaret(textarea, value, onChange, `[${clean}](${cleanUrl})`);
  };

  const handleImageInsert = (snippet, isWrap) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    // Block images live on their own paragraph line; wrap images sit inline in a text paragraph.
    const wrapped = isWrap ? `${snippet} ` : `\n\n${snippet}\n\n`;
    insertAtCaret(textarea, value, onChange, wrapped);
  };

  const insertItalic = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    wrapSelectionItalic(textarea, value, onChange);
  };

  const btn =
    "mono text-[10px] uppercase tracking-[0.2em] border border-neutral-300 px-3 py-1 hover:border-black transition-colors";

  return (
    <>
      <div className="mt-2 flex gap-2 items-center flex-wrap" data-testid="markdown-toolbar">
        {!hideLink && (
          <button type="button" onClick={insertLink} data-testid="md-insert-link" className={btn}>
            + Link
          </button>
        )}
        <button type="button" onClick={insertItalic} data-testid="md-insert-italic" className={btn}>
          <span className="italic">+ Italic</span>
        </button>
        {!linkOnly && (
          <button
            type="button"
            onClick={() => setImgOpen(true)}
            data-testid="md-insert-image"
            className={btn}
          >
            + Image
          </button>
        )}
        <span className="mono text-[10px] uppercase tracking-[0.2em] text-neutral-400 self-center ml-1">
          Insertions happen at the cursor
        </span>
      </div>
      {!linkOnly && imgOpen && (
        <ImageInsertModal onClose={() => setImgOpen(false)} onInsert={handleImageInsert} />
      )}
    </>
  );
}
